import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Users, FileText, ScanLine, BarChart3, Plus, ChevronRight, Tag } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getDashboardStats();
        setStats(res.data);
      } catch { toast.error('Erreur de chargement du tableau de bord'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const statCards = [
    { label: 'Élèves', value: stats?.students_count || 0, icon: Users, color: 'text-blue-400', link: '/students' },
    { label: 'QCM', value: stats?.quizzes_count || 0, icon: FileText, color: 'text-emerald-400', link: '/quizzes' },
    { label: 'Copies scannées', value: stats?.results_count || 0, icon: ScanLine, color: 'text-amber-400', link: '/results' },
    { label: 'Classes', value: stats?.classes_count || 0, icon: BarChart3, color: 'text-rose-400', link: '/students' },
  ];

  const gradeColor = (note) => {
    if (note >= 16) return 'text-emerald-400';
    if (note >= 12) return 'text-blue-400';
    if (note >= 10) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">
            Bonjour, {user?.name?.split(' ')[0] || 'Prof'}
          </h1>
          <p className="text-muted-foreground mt-1">Voici un aperçu de votre activité.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/quizzes/new">
            <Button data-testid="new-quiz-btn" className="bg-primary text-primary-foreground hover:bg-blue-600">
              <Plus className="w-4 h-4 mr-2" /> Nouveau QCM
            </Button>
          </Link>
          <Link to="/scan">
            <Button data-testid="scan-btn" variant="secondary" className="border border-white/5">
              <ScanLine className="w-4 h-4 mr-2" /> Scanner
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        {statCards.map((s) => (
          <Link to={s.link} key={s.label}>
            <Card className="bg-card border-border hover:border-primary/50 transition-all duration-300 hover:translate-y-[-2px] cursor-pointer" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <s.icon className={`w-5 h-5 ${s.color}`} strokeWidth={1.5} />
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-black">{loading ? '...' : s.value}</p>
                <p className="text-sm text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="text-lg">Actions rapides</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Link to="/students" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors">
              <Users className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
              <span className="text-sm">Gérer les élèves & importer CSV</span>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
            <Link to="/quizzes/new" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors">
              <FileText className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              <span className="text-sm">Créer un nouveau QCM</span>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
            <Link to="/scan" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors">
              <ScanLine className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
              <span className="text-sm">Scanner des copies</span>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
            <Link to="/tags" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors" data-testid="quick-action-tags">
              <Tag className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              <span className="text-sm">Gérer les notions pédagogiques</span>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Derniers résultats</CardTitle>
              <Link to="/results">
                <Button variant="ghost" size="sm" className="text-xs">Tout voir</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_results?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_results.map((r) => (
                  <div key={r.exam_id || r.student_name} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <div>
                      <p className="text-sm font-medium">{r.student_name}</p>
                      <p className="text-xs text-muted-foreground">{r.classe}</p>
                    </div>
                    <span className={`text-lg font-black ${gradeColor(r.note)}`}>
                      {r.note}/{r.bareme}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                Aucun résultat pour le moment. Commencez par scanner une copie.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
